import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column } from '../../../components/AsyncTable';
import { ActionButtonsContainer } from '../../../components/TablePaginator/styles';
import { ProductImagesIcon, ViewDetailsIcon } from '../../../icons';
import ContentContainer from '../../../templates/Content/ContentContainer';
import type { Product } from '../../../types';
import { productManagementAPI } from '../../../services/requests';
import getGenericRequestPageForAsyncTable from '../../../utils/TablesFunctions';
import checkPermission from '../../../utils/checkPermission';
import { PermissionsEnum } from '../../../enums/permissions.enum';
import ProductDetailsModal from './components/ProductDetailsModal';
import ProductImageModal from './components/ProductImageModal';
import ProductImageThumbnail from './components/ProductImageThumbnail';
import ProductImagesViewerModal from './components/ProductImagesViewerModal';

const ProductsPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showImagesViewer, setShowImagesViewer] = useState(false);
  const [updateTable, setUpdateTable] = useState(0);

  const closeModals = () => {
    setSelectedProduct(null);
    setShowDetailsModal(false);
    setShowImageModal(false);
    setShowImagesViewer(false);
  };

  const openDetails = (product: Product) => {
    setSelectedProduct(product);
    setShowDetailsModal(true);
  };

  const openImageEditor = (product: Product) => {
    setSelectedProduct(product);
    setShowImageModal(true);
  };

  const openImagesViewer = (product: Product) => {
    setSelectedProduct(product);
    setShowImagesViewer(true);
  };

  const handleImageCallback = () => {
    setUpdateTable((current) => current + 1);
  };

  const statusCell = (row: Product) => (
    <span
      style={{
        color: row.ATIVO?.toUpperCase() === 'S' ? theme.colors.positive : theme.colors.negative,
        fontWeight: 600,
      }}
    >
      {row.ATIVO?.toUpperCase() === 'S' ? t('active') : t('inactive')}
    </span>
  );

  const actionsCell = (row: Product) => (
    <ActionButtonsContainer type={4}>
      {checkPermission(PermissionsEnum.PRODUCTS_SHOW) && (
        <ViewDetailsIcon title={t('view')} onClick={() => openDetails(row)} />
      )}
      {checkPermission(PermissionsEnum.PRODUCTS_EDIT) && (
        <ProductImagesIcon title={t('changeProductImage')} onClick={() => openImageEditor(row)} />
      )}
    </ActionButtonsContainer>
  );

  const columns: Column<Product>[] = [
    {
      label: t('image') as string,
      Cell: (row) => {
        const images = row.images?.length ? row.images : row.image ? [row.image] : [];
        return (
          <ProductImageThumbnail
            image={images[0]}
            imageCount={images.length}
            alt={row.Descricao}
            onClick={() => openImagesViewer(row)}
          />
        );
      },
      cellStyle: { width: '96px' },
    },
    {
      label: t('description') as string,
      Cell: (row) => <span title={row.Descricao}>{row.Descricao || '-'}</span>,
    },
    {
      label: t('productCode') as string,
      accessor: 'CodigoProduto',
    },
    {
      label: t('productDetails.fields.stockColumn') as string,
      Cell: (row) => <span>{row.QtdEstoque ?? '-'}</span>,
    },
    {
      label: t('status') as string,
      Cell: statusCell,
    },
    {
      label: t('actions') as string,
      Cell: actionsCell,
      cellStyle: { width: '130px' },
    },
  ];

  return (
    <ContentContainer title={t('products')}>
      <AsyncTable
        tableName={t('products')}
        columns={columns}
        value={products}
        onChange={setProducts}
        requestPage={getGenericRequestPageForAsyncTable(productManagementAPI)}
        reqListener={[updateTable]}
        options={{
          styles: {
            primaryColor: theme.colors.surface,
            secondaryColor: theme.colors.onSurface,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />

      {showDetailsModal && selectedProduct && (
        <ProductDetailsModal product={selectedProduct} closeModal={closeModals} />
      )}

      {showImageModal && selectedProduct && (
        <ProductImageModal
          product={selectedProduct}
          closeModal={closeModals}
          callback={handleImageCallback}
        />
      )}

      {showImagesViewer && selectedProduct && (
        <ProductImagesViewerModal product={selectedProduct} closeModal={closeModals} />
      )}
    </ContentContainer>
  );
};

export default ProductsPage;
